import { Markup } from '../../models/markup';

export enum MarkupTool {
  FREEHAND = 'FREEHAND',
  LINE = 'LINE',
  RECTANGLE = 'RECTANGLE',
  CIRCLE = 'CIRCLE',
  CLOUD = 'CLOUD',
  POLYGON = 'POLYGON',
  TEXT = 'TEXT',
}

export type StrokeSize = 'S' | 'M' | 'L';

export interface MarkupEditorConfig {
  imageUrl: string;
  originalUrl?: string;       // Original PDF URL for client-side rendering
  attachmentId: string;
  existingMarkup?: Markup;
  pageNumber?: number;
  readOnly?: boolean;
  totalPages?: number;
  isDocument?: boolean;
  filename?: string;
  /**
   * Drawing sheet redline mode. When set, the editor renders only `pageNumber` of `originalUrl`,
   * edits the page's untagged row of this visibility (the Shared/Private toggle switches rows,
   * never a row's visibility), saves with `expected_version`, prompts on a version conflict and
   * uploads a strokes-only PNG preview. Leave undefined for photos and documents.
   */
  visibility?: 'shared' | 'private';
  /**
   * Sheet mode for one record's design-context link (slice 3): edit the page's shared row tagged with this link
   * instead of the untagged row, and save new rows with `link_uuid`. No Shared/Private toggle.
   */
  linkUuid?: string | null;
  /**
   * With `linkUuid` unset: the link is not saved yet, so Save makes no request and emits the strokes (and a
   * strokes-only preview) for the caller to post once the record and its link exist. `existingMarkup` re-opens them.
   */
  deferSave?: boolean;
  /**
   * Sheet mode: edit this as-built mark (its author, while it is open) with a PUT, instead of the page's layer. No
   * Field note / As-built choice and no Shared/Private toggle.
   */
  asBuiltMarkup?: Markup | null;
  /**
   * W51, sheet mode from a record's location picker: the record's point on the page (0-1 of the page, top-left
   * origin), drawn as the location pin so the user sees where the record is. Not a markup object: never saved.
   */
  contextPin?: { x: number; y: number } | null;
  /**
   * Sheet mode for a record's markup-only location (no pin): Save with every stroke removed asks this first, since
   * the location goes with the markup. Cancel keeps the editor open with nothing saved.
   */
  confirmEmptySave?: { title: string; message: string; confirm: string } | null;
  /** Sheet mode: Save with every stroke removed is refused with this line (a markup-only location edited off its record) */
  emptySaveError?: string | null;
  /**
   * 7.2, a record's markup (`linkUuid` / `deferSave`): the Field note / As-built choice it starts on when the record has
   * no markup on the page yet (As-built for an issue in an as-built category). Default note.
   */
  recordKind?: 'note' | 'as_built' | null;
}

export interface MarkupSaveResult {
  markup: Markup;
  previewDataUrl: string;
  updatedPreviewUrl?: string;
  updatedThumbUrl?: string;
  markedUpUrl?: string;
}
